import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Observable, Subject, forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Nav } from '../nav/nav';
import { TopHeader } from '../top-header/top-header';
import { CommonDropdownComponent } from '../core/ui/common-dropdown/common-dropdown.component';
import { ContentSkeletonComponent } from '../core/ui/content-skeleton/content-skeleton.component';
import { DetailAssignComponent } from '../renter/assign-flat/detail-assign/detail-assign.component';
import { AssignSaveEvent, NewEditAssignComponent } from '../renter/assign-flat/new-edit-assign/new-edit-assign.component';
import { YearlyReportModalComponent } from './yearly-report-modal/yearly-report-modal.component';
import {
  MakePaymentComponent,
  PaymentCreatePrefillContext,
  PaymentSaveEvent
} from '../transaction/payment-list/make-payment/make-payment.component';
import { APP_DEVELOPED_BY, DASHBOARD_YEAR_MIN } from '../core/constants/app-constants';
import { MONTH_TRANSLATION_KEYS, SHORT_MONTH_TRANSLATION_KEYS } from '../core/i18n/app-translations';
import { TranslatePipe } from '../core/pipes/translate.pipe';
import { LanguageService } from '../core/services/language.service';
import {
  AppUserService,
  AttachmentService,
  TokenStorageService,
  DashboardPeriodService,
  ExpenseService,
  FlatRenterMappingService,
  FlatService,
  HouseService,
  PaymentService,
  RenterService,
  SubscriptionService,
  SubscriptionUsageSummary
} from '../core/services';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, MatSnackBarModule, Nav, TopHeader, CommonDropdownComponent, ContentSkeletonComponent, MakePaymentComponent, DetailAssignComponent, NewEditAssignComponent, YearlyReportModalComponent, TranslatePipe],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.css',
})
export class Dashboard implements OnInit, OnDestroy {
  private readonly pendingPageSize = 20;
  isLoading = false;
  isYearlyLoading = false;
  isPendingLoading = false;
  isSnapshotLoading = false;
  error: string | null = null;

  selectedMonth = new Date().getMonth();
  selectedYear = new Date().getFullYear();

  collectionAmount = 0;
  collectionRows = 0;
  expenseAmount = 0;
  expenseRows = 0;
  netCollection = 0;
  dueAmount = 0;
  dueRows = 0;
  isHouseRentOnly = false;

  houseCount = 0;
  flatCount = 0;
  totalRenterCount = 0;
  availableRenterCount = 0;
  availableFlatCount = 0;
  availableRenterRows: Array<{
    id: number | null;
    name: string;
    mobile: string;
    email: string;
    photoId: string;
    source: Record<string, any>;
  }> = [];
  availableFlatRows: Array<{
    id: number | null;
    flat: string;
    house: string;
    floor: string;
    rent: number;
    payDay: string;
    source: Record<string, any>;
  }> = [];
  snapshotModalType: 'renters' | 'flats' | null = null;
  isSnapshotAssignOpen = false;
  selectedSnapshotFlat: Record<string, any> | null = null;
  selectedSnapshotRenter: Record<string, any> | null = null;

  monthlyPendingRows: Array<{
    mappingId: number | null;
    houseId: number | null;
    flatId: number | null;
    renterId: number | null;
    house: string;
    floor: string;
    flat: string;
    renter: string;
    month: string;
    monthKeys: string[];
    amount: number;
    mapping: any;
  }> = [];
  pendingFilterHouse = '';
  pendingFilterFloor = '';
  pendingFilterFlat = '';
  visiblePendingRowCount = this.pendingPageSize;

  yearlySeries: number[] = Array.from({ length: 12 }).map(() => 0);
  yearlyExpenseHeadBreakdowns: Record<number, Array<{ label: string; value: number; color: string }>> = {};
  yearlyMonthBreakdowns: Record<number, {
    rent: number;
    gas: number;
    electric: number;
    water: number;
    service: number;
    other: number;
    advanceAdjusted: number;
    grandTotal: number;
  }> = {};
  yearlyMetric: 'collection' | 'expense' = 'collection';
  yearlyGraphYear = new Date().getFullYear();
  yearlyHouseId: number | null = null;
  yearlyFlatId: number | null = null;
  private yearlyCollectionRawRows: any[] = [];
  readonly yearOptions = this.createYearOptions();
  readonly yearlyGraphYearOptions = this.yearOptions.map((year) => ({ value: year, label: year }));
  readonly yearlyMetricOptions = [
    { value: 'collection', label: 'dashboard.metric.collection' },
    { value: 'expense', label: 'dashboard.metric.expense' }
  ];
  readonly todayLabel = this.formatDateLabel(new Date());
  showDashboardInfo = false;
  isPendingDetailsOpen = false;
  selectedPendingMapping: any | null = null;
  showYearlyBreakdownModal = false;
  selectedYearlyBreakdownMonthIndex: number | null = null;
  isPendingPaymentModalOpen = false;
  pendingPaymentPrefillContext: PaymentCreatePrefillContext | null = null;
  subscriptionSummary: SubscriptionUsageSummary | null = null;
  isYearlyReportModalOpen = false;
  isYearlyReportLoading = false;
  yearlyReportCollectionRows: Array<{
    monthNo: number;
    monthLabel: string;
    renterName: string;
    paymentDateLabel: string;
    rentAmount: number;
    gasAmount: number;
    electricAmount: number;
    waterAmount: number;
    serviceAmount: number;
    negativeChargeAmount: number;
    advanceAdjustedAmount: number;
    totalAmount: number;
    expenseHeadName: string;
    expenseDateLabel: string;
    expenseAmount: number;
  }> = [];
  yearlyReportExpenseRows: Array<{
    monthNo: number;
    monthLabel: string;
    renterName: string;
    expenseHeadName: string;
    expenseDateLabel: string;
    expenseAmount: number;
  }> = [];
  yearlyReportHouseLabel = 'N/A';
  yearlyReportHouseNo = 'N/A';
  yearlyReportHouseAddress = 'N/A';
  yearlyReportFlatLabel = 'N/A';
  yearlyReportFloorLabel = 'N/A';
  yearlyReportNegativeChargeTitle = 'Negative Charge';
  yearlyReportBrandName = 'HTALLY';
  yearlyReportBrandLogoUrl: string | null = null;
  yearlyReportGeneratedOn = 'N/A';
  yearlyReportGeneratedBy = 'N/A';
  readonly yearlyReportDevelopedBy = APP_DEVELOPED_BY;
  currentUserFullName = 'N/A';
  private monthlyCollectionGrandAmount = 0;
  private monthlyCollectionRentAmount = 0;
  private monthlyDueGrandAmount = 0;
  private monthlyDueRentAmount = 0;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly snackBar: MatSnackBar,
    private readonly language: LanguageService,
    private readonly appUsers: AppUserService,
    private readonly tokens: TokenStorageService,
    private readonly dashboardPeriod: DashboardPeriodService,
    private readonly attachments: AttachmentService,
    private readonly payments: PaymentService,
    private readonly expenses: ExpenseService,
    private readonly renters: RenterService,
    private readonly houses: HouseService,
    private readonly flats: FlatService,
    private readonly mappings: FlatRenterMappingService,
    private readonly subscriptionService: SubscriptionService
  ) {}

  ngOnInit(): void {
    this.resetYearlyReportState();
    this.dashboardPeriod.period$
      .pipe(takeUntil(this.destroy$))
      .subscribe((period) => {
        this.selectedMonth = Number(period.month);
        this.selectedYear = Number(period.year);
        this.resetPendingFilters();
        this.loadDashboard();
        this.loadMonthlyPendingList();
      });
    this.loadStaticCounts();
    this.loadYearlyGraph();
    this.loadCurrentUserFullName();
    this.loadYearlyReportBranding();
    this.loadSubscriptionSummary();
    this.subscriptionService.summaryRefresh$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.loadSubscriptionSummary());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get monthNames(): string[] {
    return SHORT_MONTH_TRANSLATION_KEYS.map((key) => this.t(key, key));
  }

  get fullMonthNames(): string[] {
    return MONTH_TRANSLATION_KEYS.map((key) => this.t(key, key));
  }

  get maxYearlyValue(): number {
    const max = Math.max(...this.yearlySeries, 0);
    return max <= 0 ? 1 : max;
  }

  barHeight(value: number): number {
    const numeric = Number(value ?? 0);
    if (!Number.isFinite(numeric) || numeric <= 0) {
      return 0;
    }
    return Math.round((numeric / this.maxYearlyValue) * 100);
  }

  get yearlyMetricLabel(): string {
    return this.yearlyMetric === 'expense'
      ? 'dashboard.metric.expense'
      : 'dashboard.metric.collection';
  }

  get yearlyTotalAmount(): number {
    return this.yearlySeries.reduce((sum, value) => sum + Number(value ?? 0), 0);
  }

  get canOpenYearlyReport(): boolean {
    return Number.isFinite(Number(this.yearlyFlatId))
      && Number(this.yearlyFlatId) > 0
      && Number(this.yearlyTotalAmount ?? 0) > 0;
  }

  get isSnapshotModalOpen(): boolean {
    return this.snapshotModalType !== null;
  }

  get snapshotModalTitle(): string {
    return this.snapshotModalType === 'flats'
      ? this.t('dashboard.availableFlats', 'Available Flats')
      : this.t('dashboard.availableRenter', 'Available Renter');
  }

  get snapshotModalCount(): number {
    return this.snapshotModalType === 'flats'
      ? this.availableFlatRows.length
      : this.availableRenterRows.length;
  }

  get snapshotModalEmptyMessage(): string {
    return this.snapshotModalType === 'flats'
      ? 'No available flats found.'
      : 'No available renters found.';
  }

  get yearlyReportTotalAmount(): number {
    return this.yearlyReportCollectionRows.reduce((sum, item) => sum + Number(item?.totalAmount ?? 0), 0);
  }

  get yearlyReportExpenseTotalAmount(): number {
    return this.yearlyReportExpenseRows.reduce((sum, item) => sum + Number(item?.expenseAmount ?? 0), 0);
  }

  get selectedYearlyBreakdownTitle(): string {
    if (this.selectedYearlyBreakdownMonthIndex === null || this.selectedYearlyBreakdownMonthIndex < 0) {
      return '';
    }
    return `${this.fullMonthNames[this.selectedYearlyBreakdownMonthIndex]} ${this.yearlyGraphYear}`;
  }

  get selectedYearlyBreakdownData(): {
    rent: number;
    gas: number;
    electric: number;
    water: number;
    service: number;
    other: number;
    advanceAdjusted: number;
    grandTotal: number;
  } {
    if (this.selectedYearlyBreakdownMonthIndex === null || this.selectedYearlyBreakdownMonthIndex < 0) {
      return {
        rent: 0,
        gas: 0,
        electric: 0,
        water: 0,
        service: 0,
        other: 0,
        advanceAdjusted: 0,
        grandTotal: 0
      };
    }
    return this.yearlyMonthBreakdowns[this.selectedYearlyBreakdownMonthIndex] ?? {
      rent: 0,
      gas: 0,
      electric: 0,
      water: 0,
      service: 0,
      other: 0,
      advanceAdjusted: 0,
      grandTotal: 0
    };
  }

  get selectedYearlyBreakdownLegend(): Array<{ label: string; value: number; color: string }> {
    if (this.yearlyMetric === 'expense') {
      const monthIndex = this.selectedYearlyBreakdownMonthIndex ?? -1;
      if (monthIndex < 0) {
        return [];
      }
      return this.yearlyExpenseHeadBreakdowns[monthIndex] ?? [];
    }

    const data = this.selectedYearlyBreakdownData;
    return [
      { label: this.t('dashboard.legend.rent', 'Rent'), value: Number(data.rent ?? 0), color: '#1a98b6' },
      { label: this.t('dashboard.legend.gas', 'Gas'), value: Number(data.gas ?? 0), color: '#32b87f' },
      { label: this.t('dashboard.legend.electric', 'Electric'), value: Number(data.electric ?? 0), color: '#f2a93b' },
      { label: this.t('dashboard.legend.water', 'Water'), value: Number(data.water ?? 0), color: '#4e7de0' },
      { label: this.t('dashboard.legend.serviceOther', 'Service + Other'), value: Number(data.service ?? 0), color: '#8f63d6' },
      { label: this.t('dashboard.legend.negativeCharge', 'Negative Charge (-)'), value: Number(data.other ?? 0), color: '#f07f6b' },
      { label: this.t('dashboard.legend.advanceAdjusted', 'Advance Adjusted (-)'), value: Number(data.advanceAdjusted ?? 0), color: '#8f9cbb' }
    ].filter((item) => Number(item.value) > 0);
  }

  get selectedYearlyBreakdownPieStyle(): Record<string, string> {
    const slices = this.selectedYearlyBreakdownLegend;
    const total = slices.reduce((sum, item) => sum + Number(item.value ?? 0), 0);
    if (!Number.isFinite(total) || total <= 0) {
      return { background: 'conic-gradient(#e9eef7 0 360deg)' };
    }

    let start = 0;
    const parts: string[] = [];
    slices.forEach((slice) => {
      const ratio = Number(slice.value) / total;
      const end = start + (ratio * 360);
      parts.push(`${slice.color} ${start}deg ${end}deg`);
      start = end;
    });
    return {
      background: `conic-gradient(${parts.join(', ')})`
    };
  }

  get filteredMonthlyPendingRows(): Array<{
    mappingId: number | null;
    houseId: number | null;
    flatId: number | null;
    renterId: number | null;
    house: string;
    floor: string;
    flat: string;
    renter: string;
    month: string;
    monthKeys: string[];
    amount: number;
    mapping: any;
  }> {
    const house = this.pendingFilterHouse.trim().toLowerCase();
    const floor = this.pendingFilterFloor.trim().toLowerCase();
    const flat = this.pendingFilterFlat.trim().toLowerCase();

    return this.monthlyPendingRows.filter((item) => {
      if (house && !item.house.toLowerCase().includes(house)) {
        return false;
      }
      if (floor && !item.floor.toLowerCase().includes(floor)) {
        return false;
      }
      if (flat && !item.flat.toLowerCase().includes(flat)) {
        return false;
      }
      return true;
    });
  }

  get visibleFilteredMonthlyPendingRows(): Array<{
    mappingId: number | null;
    houseId: number | null;
    flatId: number | null;
    renterId: number | null;
    house: string;
    floor: string;
    flat: string;
    renter: string;
    month: string;
    monthKeys: string[];
    amount: number;
    mapping: any;
  }> {
    return this.filteredMonthlyPendingRows.slice(0, this.visiblePendingRowCount);
  }

  get pendingHouseOptions(): string[] {
    return this.uniquePendingValues('house');
  }

  get pendingFloorOptions(): string[] {
    return this.uniquePendingValues('floor');
  }

  get pendingFlatOptions(): string[] {
    return this.uniquePendingValues('flat');
  }

  get pendingHouseDropdownOptions(): Array<{ value: string; label: string }> {
    return [
      { value: '', label: 'dashboard.allHouses' },
      ...this.pendingHouseOptions.map((item) => ({ value: item, label: item }))
    ];
  }

  get pendingFloorDropdownOptions(): Array<{ value: string; label: string }> {
    return [
      { value: '', label: 'dashboard.allFloors' },
      ...this.pendingFloorOptions.map((item) => ({ value: item, label: item }))
    ];
  }

  get pendingFlatDropdownOptions(): Array<{ value: string; label: string }> {
    return [
      { value: '', label: 'dashboard.allFlats' },
      ...this.pendingFlatOptions.map((item) => ({ value: item, label: item }))
    ];
  }

  formatAmount(value: number): string {
    return Number(value ?? 0).toLocaleString('en-US');
  }

  formatDate(value: string | null | undefined): string {
    if (!value) {
      return this.t('common.na', 'N/A');
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return `${value}`;
    }
    const day = `${date.getDate()}`.padStart(2, '0');
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  get pendingMonthLabel(): string {
    return `${this.fullMonthNames[this.selectedMonth]} ${this.selectedYear}`;
  }

  get pendingCollectionRangeLabel(): string {
    const previous = this.getPreviousMonthYear(this.selectedMonth, this.selectedYear);
    const previousLabel = `${this.fullMonthNames[previous.month]} ${previous.year}`;
    const currentLabel = `${this.fullMonthNames[this.selectedMonth]} ${this.selectedYear}`;
    return `${previousLabel} - ${currentLabel}`;
  }

  get pendingCollectionRangeShortLabel(): string {
    if (!this.hasBothPendingRangeMonths()) {
      return '';
    }
    const previous = this.getPreviousMonthYear(this.selectedMonth, this.selectedYear);
    const previousLabel = this.monthNames[previous.month];
    const currentLabel = this.monthNames[this.selectedMonth];
    return `${previousLabel} - ${currentLabel}`;
  }

  get isAtCurrentMonth(): boolean {
    const now = new Date();
    return this.selectedYear === now.getFullYear() && this.selectedMonth === now.getMonth();
  }

  previousPendingMonth(): void {
    this.resetPendingFilters();
    const month = this.selectedMonth === 0 ? 11 : this.selectedMonth - 1;
    const year = this.selectedMonth === 0 ? this.selectedYear - 1 : this.selectedYear;
    this.dashboardPeriod.setPeriod({ month, year });
  }

  nextPendingMonth(): void {
    if (this.isAtCurrentMonth) {
      return;
    }
    this.resetPendingFilters();
    const month = this.selectedMonth === 11 ? 0 : this.selectedMonth + 1;
    const year = this.selectedMonth === 11 ? this.selectedYear + 1 : this.selectedYear;
    this.dashboardPeriod.setPeriod({ month, year });
  }

  onPendingDropdownFilterChange(event: { name: string; value: unknown }): void {
    const value = `${event?.value ?? ''}`.trim();
    switch (event?.name) {
      case 'pendingHouse':
        this.pendingFilterHouse = value;
        break;
      case 'pendingFloor':
        this.pendingFilterFloor = value;
        break;
      case 'pendingFlat':
        this.pendingFilterFlat = value;
        break;
      default:
        break;
    }
    this.resetPendingVisibleRows();
  }

  onPendingListScroll(event: Event): void {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    const reachedBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 40;
    if (!reachedBottom || this.visiblePendingRowCount >= this.filteredMonthlyPendingRows.length) {
      return;
    }

    this.visiblePendingRowCount += this.pendingPageSize;
  }

  openPaymentFromPending(item: { mappingId: number | null; houseId: number | null; flatId: number | null; renterId: number | null }): void {
    this.pendingPaymentPrefillContext = {
      pendingMappingId: item?.mappingId ?? null,
      houseId: item?.houseId ?? null,
      flatId: item?.flatId ?? null,
      renterId: item?.renterId ?? null,
      paymentMonth: this.selectedMonth + 1,
      paymentYear: this.selectedYear
    };
    this.isPendingPaymentModalOpen = true;
  }

  openPendingDetails(item: { mapping?: any }): void {
    this.selectedPendingMapping = item?.mapping ?? null;
    this.isPendingDetailsOpen = !!this.selectedPendingMapping;
  }

  closePendingDetails(): void {
    this.isPendingDetailsOpen = false;
    this.selectedPendingMapping = null;
  }

  openAvailableRenterModal(): void {
    this.snapshotModalType = 'renters';
  }

  openAvailableFlatModal(): void {
    this.snapshotModalType = 'flats';
  }

  closeSnapshotModal(): void {
    this.snapshotModalType = null;
  }

  openAssignRenterFromAvailableFlat(flat: { source?: Record<string, any> }, event?: Event): void {
    event?.stopPropagation();
    this.selectedSnapshotFlat = flat?.source ? { ...flat.source } : null;
    this.selectedSnapshotRenter = null;
    this.snapshotModalType = null;
    this.isSnapshotAssignOpen = !!this.selectedSnapshotFlat;
  }

  openAssignFlatFromAvailableRenter(renter: { source?: Record<string, any> }, event?: Event): void {
    event?.stopPropagation();
    this.selectedSnapshotRenter = renter?.source ? { ...renter.source } : null;
    this.selectedSnapshotFlat = null;
    this.snapshotModalType = null;
    this.isSnapshotAssignOpen = !!this.selectedSnapshotRenter;
  }

  closeSnapshotAssignModal(): void {
    this.isSnapshotAssignOpen = false;
    this.selectedSnapshotFlat = null;
    this.selectedSnapshotRenter = null;
  }

  onSnapshotAssignSaved(_event?: AssignSaveEvent): void {
    this.closeSnapshotAssignModal();
    this.loadStaticCounts();
    this.loadMonthlyPendingList();
  }

  openPaymentFromSelectedPendingMapping(): void {
    if (this.isPendingPaymentBlocked()) {
      this.openPendingPaymentBlockedToast();
      return;
    }
    if (!this.selectedPendingMapping) {
      return;
    }
    const payload = {
      mappingId: Number.isFinite(Number(this.selectedPendingMapping?.id)) ? Number(this.selectedPendingMapping.id) : null,
      houseId: Number.isFinite(Number(this.selectedPendingMapping?.flat?.house?.id)) ? Number(this.selectedPendingMapping.flat.house.id) : null,
      flatId: Number.isFinite(Number(this.selectedPendingMapping?.flat?.id)) ? Number(this.selectedPendingMapping.flat.id) : null,
      renterId: Number.isFinite(Number(this.selectedPendingMapping?.renter?.id)) ? Number(this.selectedPendingMapping.renter.id) : null
    };
    this.closePendingDetails();
    this.openPaymentFromPending(payload);
  }

  remainingAdvanceAmount(item: any): number {
    const advance = Number(item?.advanceAmount ?? 0);
    const used = Number(item?.usedAdvanceAmount ?? 0);
    return Math.max(0, advance - used);
  }

  openPendingAttachment(attachment: any): void {
    const attachmentId = Number(attachment?.id);
    if (!Number.isFinite(attachmentId)) {
      return;
    }

    this.attachments.previewUrlByAttachmentId(attachmentId).subscribe({
      next: (url: string) => {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer';
        anchor.click();
      }
    });
  }

  closePendingPaymentModal(): void {
    this.isPendingPaymentModalOpen = false;
    this.pendingPaymentPrefillContext = null;
  }

  onPendingPaymentSaved(_event: PaymentSaveEvent): void {
    this.loadDashboard();
    this.loadMonthlyPendingList();
    this.isPendingPaymentModalOpen = false;
    this.pendingPaymentPrefillContext = null;
  }

  onBlockedPendingPaymentClick(): void {
    this.openPendingPaymentBlockedToast();
  }

  toggleDashboardInfo(): void {
    this.showDashboardInfo = !this.showDashboardInfo;
  }

  closeDashboardInfo(): void {
    this.showDashboardInfo = false;
  }

  onYearlyMetricChange(value: string): void {
    this.yearlyMetric = value === 'expense' ? 'expense' : 'collection';
    this.loadYearlyGraph();
  }

  onHouseRentOnlyToggle(): void {
    this.applyMonthlySummaryMode();
    if (this.yearlyMetric === 'collection') {
      if (this.yearlyCollectionRawRows.length > 0) {
        const parsed = this.parseYearlyPaymentFromList(this.yearlyCollectionRawRows);
        this.yearlySeries = parsed.series;
        this.yearlyMonthBreakdowns = parsed.breakdowns;
        this.yearlyExpenseHeadBreakdowns = {};
      } else {
        this.loadYearlyGraph();
      }
    }
  }

  onYearlyYearChange(value: string): void {
    const parsed = Number(value);
    this.yearlyGraphYear = Number.isFinite(parsed) ? parsed : new Date().getFullYear();
    this.loadYearlyGraph();
  }

  onYearlyDropdownChange(event: { name: string; value: unknown }): void {
    if (event?.name === 'yearlyMetric') {
      this.onYearlyMetricChange(String(event.value ?? 'collection'));
      return;
    }

    if (event?.name === 'yearlyGraphYear') {
      this.onYearlyYearChange(String(event.value ?? this.yearlyGraphYear));
      return;
    }

    if (event?.name === 'yearlyHouseId') {
      const nextHouseId = event?.value === null || event?.value === undefined || event?.value === ''
        ? null
        : Number(event.value);
      this.yearlyHouseId = Number.isFinite(nextHouseId as number) ? (nextHouseId as number) : null;
      this.yearlyFlatId = null;
      this.loadYearlyGraph();
      return;
    }

    if (event?.name === 'yearlyFlatId') {
      const nextFlatId = event?.value === null || event?.value === undefined || event?.value === ''
        ? null
        : Number(event.value);
      this.yearlyFlatId = Number.isFinite(nextFlatId as number) ? (nextFlatId as number) : null;
      this.loadYearlyGraph();
    }
  }

  onYearlyReportButtonClick(): void {
    if (!Number.isFinite(Number(this.yearlyFlatId)) || Number(this.yearlyFlatId) <= 0) {
      this.snackBar.open(this.t('dashboard.selectFlatFirst', 'Select flat first.'), this.t('common.close', 'Close'), {
        duration: 2200,
        horizontalPosition: 'right',
        verticalPosition: 'top',
        panelClass: ['dashboard-toast-warning']
      });
      return;
    }

    if (Number(this.yearlyTotalAmount ?? 0) <= 0) {
      this.snackBar.open(this.t('dashboard.noDataForFlat', 'No data available for the flat.'), this.t('common.close', 'Close'), {
        duration: 2400,
        horizontalPosition: 'right',
        verticalPosition: 'top',
        panelClass: ['dashboard-toast-warning']
      });
      return;
    }

    this.openYearlyReportModal();
  }

  closeYearlyReportModal(): void {
    this.isYearlyReportModalOpen = false;
    this.isYearlyReportLoading = false;
    this.yearlyReportCollectionRows = [];
    this.yearlyReportExpenseRows = [];
  }

  private openYearlyReportModal(): void {
    this.isYearlyReportModalOpen = true;
    this.isYearlyReportLoading = true;
    this.yearlyReportCollectionRows = [];
    this.yearlyReportExpenseRows = [];
    this.yearlyReportHouseLabel = this.t('common.na', 'N/A');
    this.yearlyReportHouseNo = this.t('common.na', 'N/A');
    this.yearlyReportHouseAddress = this.t('common.na', 'N/A');
    this.yearlyReportFlatLabel = this.t('common.na', 'N/A');
    this.yearlyReportFloorLabel = this.t('common.na', 'N/A');
    this.yearlyReportNegativeChargeTitle = this.t('dashboard.legend.negativeCharge', 'Negative Charge');
    this.yearlyReportGeneratedOn = this.formatDateTimeLabel(new Date());
    this.yearlyReportGeneratedBy = this.currentUserFullName || this.resolveCurrentUserLabel();

    const yearText = `${this.yearlyGraphYear}`;
    const houseId = Number(this.yearlyHouseId);
    const flatId = Number(this.yearlyFlatId);
    const hasHouse = Number.isFinite(houseId) && houseId > 0;

    const paymentFilters: Record<string, unknown> = {
      paymentYear: yearText,
      status: 1,
      'flatRenterMapping.flat.id': flatId
    };
    if (hasHouse) {
      paymentFilters['flatRenterMapping.flat.house.id'] = houseId;
    }

    const expenseFilters: Record<string, unknown> = {
      expenseYear: yearText,
      status: 1,
      'flat.id': flatId
    };
    if (hasHouse) {
      expenseFilters['house.id'] = houseId;
    }

    forkJoin({
      paymentRes: this.payments.list({
        pagination: false,
        pageNo: 0,
        rowCount: 0,
        withTotals: false,
        filters: paymentFilters
      }),
      expenseRes: this.expenses.list({
        pagination: false,
        pageNo: 0,
        rowCount: 0,
        withTotals: false,
        filters: expenseFilters
      })
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ paymentRes, expenseRes }) => {
          const paymentRows = Array.isArray(paymentRes?.data) ? paymentRes.data : [];
          const expenseRows = Array.isArray(expenseRes?.data) ? expenseRes.data : [];

          this.yearlyReportCollectionRows = paymentRows
            .map((item: any) => {
              const monthNo = Number(item?.paymentMonth);
              if (!Number.isFinite(monthNo) || monthNo < 1 || monthNo > 12) {
                return null;
              }
              const amount = (
                Number(item?.rentAmount ?? 0)
                + Number(item?.gasBillAmount ?? 0)
                + Number(item?.electricBillAmount ?? 0)
                + Number(item?.waterBillAmount ?? 0)
                + Number(item?.serviceChargeAndOtherAmount ?? 0)
                - Number(item?.negativeChargeAmount ?? 0)
                - Number(item?.advanceAdjustedAmount ?? 0)
              );
              return {
                monthNo,
                monthLabel: this.yearMonthLabel(monthNo, this.yearlyGraphYear),
                renterName: `${item?.flatRenterMapping?.renter?.renterName ?? this.t('common.na', 'N/A')}`.trim() || this.t('common.na', 'N/A'),
                paymentDateLabel: this.formatDate(item?.paymentDate),
                rentAmount: Number(item?.rentAmount ?? 0),
                gasAmount: Number(item?.gasBillAmount ?? 0),
                electricAmount: Number(item?.electricBillAmount ?? 0),
                waterAmount: Number(item?.waterBillAmount ?? 0),
                serviceAmount: Number(item?.serviceChargeAndOtherAmount ?? 0),
                negativeChargeAmount: Number(item?.negativeChargeAmount ?? 0),
                advanceAdjustedAmount: Number(item?.advanceAdjustedAmount ?? 0),
                totalAmount: Number.isFinite(amount) ? amount : 0,
                expenseHeadName: '-',
                expenseDateLabel: '-',
                expenseAmount: 0
              };
            })
            .filter((item: any) => !!item && Number(item.totalAmount ?? 0) > 0)
            .sort((a: any, b: any) => {
              const monthDiff = Number(a.monthNo) - Number(b.monthNo);
              if (monthDiff !== 0) {
                return monthDiff;
              }
              return `${a.renterName ?? ''}`.localeCompare(`${b.renterName ?? ''}`);
            });

          this.yearlyReportExpenseRows = expenseRows
            .map((item: any) => {
              const monthNo = Number(item?.expenseMonth);
              if (!Number.isFinite(monthNo) || monthNo < 1 || monthNo > 12) {
                return null;
              }
              const amount = Number(item?.expenseAmount ?? 0);
              return {
                monthNo,
                monthLabel: this.yearMonthLabel(monthNo, this.yearlyGraphYear),
                renterName: this.t('common.na', 'N/A'),
                expenseHeadName: `${item?.expenseHead?.name ?? item?.expenseHeadName ?? this.t('common.na', 'N/A')}`.trim() || this.t('common.na', 'N/A'),
                expenseDateLabel: this.formatDate(item?.expenseDate),
                expenseAmount: Number.isFinite(amount) ? amount : 0
              };
            })
            .filter((item: any) => !!item && Number(item.expenseAmount ?? 0) > 0)
            .sort((a: any, b: any) => Number(a.monthNo) - Number(b.monthNo));

          const firstPayment = paymentRows[0] ?? null;
          const firstExpense = expenseRows[0] ?? null;
          const houseInfo = firstPayment?.flatRenterMapping?.flat?.house ?? firstExpense?.house ?? firstExpense?.flat?.house ?? null;
          const flatInfo = firstPayment?.flatRenterMapping?.flat ?? firstExpense?.flat ?? null;
          if (houseInfo || flatInfo) {
            this.yearlyReportHouseLabel = `${houseInfo?.houseName ?? this.t('common.na', 'N/A')}`.trim() || this.t('common.na', 'N/A');
            this.yearlyReportHouseNo = `${houseInfo?.houseNo ?? this.t('common.na', 'N/A')}`.trim() || this.t('common.na', 'N/A');
            this.yearlyReportHouseAddress = `${houseInfo?.houseAddress ?? this.t('common.na', 'N/A')}`.trim() || this.t('common.na', 'N/A');
            this.yearlyReportFlatLabel = `${flatInfo?.flatName ?? this.t('common.na', 'N/A')}`.trim() || this.t('common.na', 'N/A');
            this.yearlyReportFloorLabel = `${flatInfo?.floorName ?? this.t('common.na', 'N/A')}`.trim() || this.t('common.na', 'N/A');
            this.yearlyReportNegativeChargeTitle = `${flatInfo?.negativeChargeName ?? ''}`.trim() || this.t('dashboard.legend.negativeCharge', 'Negative Charge');
          }
          this.isYearlyReportLoading = false;
        },
        error: () => {
          this.isYearlyReportLoading = false;
          this.yearlyReportCollectionRows = [];
          this.yearlyReportExpenseRows = [];
        }
      });
  }

  openYearlyBreakdown(monthIndex: number): void {
    if (!Number.isFinite(monthIndex) || monthIndex < 0 || monthIndex > 11) {
      return;
    }
    if (Number(this.yearlySeries[monthIndex] ?? 0) <= 0) {
      return;
    }
    this.selectedYearlyBreakdownMonthIndex = monthIndex;
    this.showYearlyBreakdownModal = true;
  }

  closeYearlyBreakdown(): void {
    this.showYearlyBreakdownModal = false;
    this.selectedYearlyBreakdownMonthIndex = null;
  }

  private loadDashboard(): void {
    this.isLoading = true;
    this.error = null;

    const { startRange, endRange } = this.monthRange(this.selectedYear, this.selectedMonth);
    const previous = this.getPreviousMonthYear(this.selectedMonth, this.selectedYear);
    const { startRange: pendingStartRange } = this.monthRange(previous.year, previous.month);
    const paymentFilters: Record<string, unknown> = {
      status: 1,
      paymentDate: [startRange, endRange]
    };
    const expenseFilters: Record<string, unknown> = {
      status: 1,
      expenseDate: [startRange, endRange]
    };

    forkJoin({
      paymentList: this.payments.list({
        pagination: true,
        pageNo: 0,
        rowCount: 8,
        withTotals: true,
        filters: paymentFilters
      }),
      paymentRentList: this.payments.list({
        pagination: false,
        pageNo: 0,
        rowCount: 0,
        withTotals: false,
        filters: paymentFilters
      }),
      paymentPendingList: this.mappings.paymentPendingList({
        withTotals: true,
        startRange: pendingStartRange,
        endRange,
        filters: { status: 1 }
      }),
      paymentPendingRentList: this.mappings.paymentPendingList({
        withTotals: false,
        startRange: pendingStartRange,
        endRange,
        filters: { status: 1 }
      }),
      expenseList: this.expenses.list({
        pagination: true,
        pageNo: 0,
        rowCount: 1,
        withTotals: true,
        filters: expenseFilters
      })
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const paymentTotals = res.paymentList?.totals || {};
          const expenseTotals = res.expenseList?.totals || {};
          const dueTotals = res.paymentPendingList?.totals || {};
          const paymentRows = Array.isArray(res.paymentRentList?.data) ? res.paymentRentList.data : [];
          const pendingRows = Array.isArray(res.paymentPendingRentList?.data) ? res.paymentPendingRentList.data : [];

          this.monthlyCollectionGrandAmount = Number(paymentTotals?.grand_total ?? 0);
          this.monthlyCollectionRentAmount = paymentRows.reduce((sum: number, item: any) => (
            sum + Math.max(0, Number(item?.rentAmount ?? 0) - Number(item?.advanceAdjustedAmount ?? 0))
          ), 0);
          this.collectionRows = Number(res.paymentList?.totalRows ?? 0);
          this.expenseAmount = Number(expenseTotals?.grand_total ?? expenseTotals?.total_expense ?? 0);
          this.expenseRows = Number(res.expenseList?.totalRows ?? 0);
          this.monthlyDueGrandAmount = Number(dueTotals?.grand_total ?? 0);
          this.monthlyDueRentAmount = pendingRows.reduce((sum: number, item: any) => (
            sum + this.calculatePendingMappingRentAmount(item)
          ), 0);
          this.applyMonthlySummaryMode();
          this.dueRows = Number(res.paymentPendingList?.totalRows ?? 0);
          this.isLoading = false;
        },
        error: (err: any) => {
          this.isLoading = false;
          this.error = err?.message || 'Failed to load dashboard data.';
        }
      });
  }

  private loadStaticCounts(): void {
    this.isSnapshotLoading = true;
    const activeFilters = { status: 1 };
    forkJoin({
      renterList: this.renters.list({
        pagination: false,
        pageNo: 0,
        rowCount: 0,
        filters: activeFilters
      }),
      houseList: this.houses.list({
        pagination: false,
        pageNo: 0,
        rowCount: 0,
        filters: activeFilters
      }),
      flatList: this.flats.list({
        pagination: false,
        pageNo: 0,
        rowCount: 0,
        filters: activeFilters
      }),
      mappingList: this.mappings.list({
        pagination: false,
        pageNo: 0,
        rowCount: 0,
        filters: activeFilters
      })
    })
      .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            this.totalRenterCount = this.resolveCount(res.renterList);
            this.houseCount = this.resolveCount(res.houseList);
            this.flatCount = this.resolveCount(res.flatList);
            this.availableRenterRows = this.buildAvailableRenterRows(res.renterList, res.mappingList);
            this.availableFlatRows = this.buildAvailableFlatRows(res.flatList, res.mappingList);
            this.availableRenterCount = this.availableRenterRows.length || this.calculateAvailableRenterCount(res.renterList, res.mappingList);
            this.availableFlatCount = this.availableFlatRows.length || this.calculateAvailableFlatCount(res.flatList, res.mappingList);
            this.isSnapshotLoading = false;
          },
          error: () => {
            this.totalRenterCount = 0;
            this.houseCount = 0;
            this.flatCount = 0;
            this.availableRenterCount = 0;
            this.availableFlatCount = 0;
            this.availableRenterRows = [];
            this.availableFlatRows = [];
            this.isSnapshotLoading = false;
          }
        });
  }

  private resolveCount(response: any): number {
    const data = Array.isArray(response?.data) ? response.data : [];
    if (data.length > 0) {
      return data.length;
    }
    return Number(response?.totalRows ?? 0);
  }

  private calculateAvailableRenterCount(renterResponse: any, mappingResponse: any): number {
    const renters = Array.isArray(renterResponse?.data) ? renterResponse.data : [];
    const mappings = Array.isArray(mappingResponse?.data) ? mappingResponse.data : [];

    const uniqueMappedRenterIds = new Set<number>();
    mappings.forEach((item: any) => {
      const renterId = Number(item?.renter?.id);
      if (Number.isFinite(renterId) && renterId > 0) {
        uniqueMappedRenterIds.add(renterId);
      }
    });

    const totalActiveRenterCount = renters.length > 0 ? renters.length : Number(renterResponse?.totalRows ?? 0);
    return Math.max(totalActiveRenterCount - uniqueMappedRenterIds.size, 0);
  }

  private calculateAvailableFlatCount(flatResponse: any, mappingResponse: any): number {
    const flats = Array.isArray(flatResponse?.data) ? flatResponse.data : [];
    const mappings = Array.isArray(mappingResponse?.data) ? mappingResponse.data : [];

    const uniqueMappedFlatIds = new Set<number>();
    mappings.filter((item: any) => this.isOpenActiveMapping(item)).forEach((item: any) => {
      const flatId = Number(item?.flat?.id);
      if (Number.isFinite(flatId) && flatId > 0) {
        uniqueMappedFlatIds.add(flatId);
      }
    });

    const totalActiveFlatCount = flats.length > 0 ? flats.length : Number(flatResponse?.totalRows ?? 0);
    return Math.max(totalActiveFlatCount - uniqueMappedFlatIds.size, 0);
  }

  private buildAvailableRenterRows(renterResponse: any, mappingResponse: any): Array<{
    id: number | null;
    name: string;
    mobile: string;
    email: string;
    photoId: string;
    source: Record<string, any>;
  }> {
    const renters = Array.isArray(renterResponse?.data) ? renterResponse.data : [];
    const mappedRenterIds = this.mappedIdSet(mappingResponse, 'renter');

    return renters
      .filter((item: any) => {
        const id = Number(item?.id);
        return !Number.isFinite(id) || !mappedRenterIds.has(id);
      })
      .map((item: any) => ({
        id: Number.isFinite(Number(item?.id)) ? Number(item.id) : null,
        name: `${item?.renterName ?? this.t('common.na', 'N/A')}`.trim() || this.t('common.na', 'N/A'),
        mobile: `${item?.renterMobile ?? this.t('common.na', 'N/A')}`.trim() || this.t('common.na', 'N/A'),
        email: `${item?.renterEmail ?? this.t('common.na', 'N/A')}`.trim() || this.t('common.na', 'N/A'),
        photoId: `${item?.renterPhotoIdNo ?? this.t('common.na', 'N/A')}`.trim() || this.t('common.na', 'N/A'),
        source: item
      }))
      .sort((
        a: { name: string },
        b: { name: string }
      ) => a.name.localeCompare(b.name));
  }

  private buildAvailableFlatRows(flatResponse: any, mappingResponse: any): Array<{
    id: number | null;
    flat: string;
    house: string;
    floor: string;
    rent: number;
    payDay: string;
    source: Record<string, any>;
  }> {
    const flats = Array.isArray(flatResponse?.data) ? flatResponse.data : [];
    const mappedFlatIds = this.openMappedIdSet(mappingResponse, 'flat');

    return flats
      .filter((item: any) => {
        const id = Number(item?.id);
        return !Number.isFinite(id) || !mappedFlatIds.has(id);
      })
      .map((item: any) => ({
        id: Number.isFinite(Number(item?.id)) ? Number(item.id) : null,
        flat: `${item?.flatName ?? this.t('common.na', 'N/A')}`.trim() || this.t('common.na', 'N/A'),
        house: `${item?.house?.houseName ?? this.t('common.na', 'N/A')}`.trim() || this.t('common.na', 'N/A'),
        floor: `${item?.floorName ?? this.t('common.na', 'N/A')}`.trim() || this.t('common.na', 'N/A'),
        rent: Number(item?.rentAmount ?? 0),
        payDay: `${item?.payDayTh ?? this.t('common.na', 'N/A')}`.trim() || this.t('common.na', 'N/A'),
        source: item
      }))
      .sort((
        a: { house: string; floor: string; flat: string },
        b: { house: string; floor: string; flat: string }
      ) => `${a.house} ${a.floor} ${a.flat}`.localeCompare(`${b.house} ${b.floor} ${b.flat}`));
  }

  private mappedIdSet(mappingResponse: any, key: 'renter' | 'flat'): Set<number> {
    const mappings = Array.isArray(mappingResponse?.data) ? mappingResponse.data : [];
    const ids = new Set<number>();
    mappings.forEach((item: any) => {
      const id = Number(item?.[key]?.id);
      if (Number.isFinite(id) && id > 0) {
        ids.add(id);
      }
    });
    return ids;
  }

  private openMappedIdSet(mappingResponse: any, key: 'renter' | 'flat'): Set<number> {
    const mappings = Array.isArray(mappingResponse?.data) ? mappingResponse.data : [];
    const ids = new Set<number>();
    mappings.filter((item: any) => this.isOpenActiveMapping(item)).forEach((item: any) => {
      const id = Number(item?.[key]?.id);
      if (Number.isFinite(id) && id > 0) {
        ids.add(id);
      }
    });
    return ids;
  }

  private isOpenActiveMapping(mapping: any): boolean {
    const status = Number(mapping?.status);
    const endDate = `${mapping?.renterEndDate ?? ''}`.trim();
    return status === 1 && !endDate;
  }

  private loadMonthlyPendingList(): void {
    this.isPendingLoading = true;
    this.resetPendingVisibleRows();
    const previous = this.getPreviousMonthYear(this.selectedMonth, this.selectedYear);
    const { startRange } = this.monthRange(previous.year, previous.month);
    const { endRange } = this.monthRange(this.selectedYear, this.selectedMonth);
    this.mappings.paymentPendingList({
      withTotals: false,
      startRange,
      endRange,
      filters: { status: 1 }
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const rows = Array.isArray(res?.data) ? res.data : [];
          this.monthlyPendingRows = rows.map((item: any) => ({
            mappingId: Number.isFinite(Number(item?.id)) ? Number(item.id) : null,
            houseId: Number.isFinite(Number(item?.flat?.house?.id)) ? Number(item.flat.house.id) : null,
            flatId: Number.isFinite(Number(item?.flat?.id)) ? Number(item.flat.id) : null,
            renterId: Number.isFinite(Number(item?.renter?.id)) ? Number(item.renter.id) : null,
            house: item?.flat?.house?.houseName || this.t('common.na', 'N/A'),
            floor: item?.flat?.floorName || this.t('common.na', 'N/A'),
            flat: item?.flat?.flatName || this.t('common.na', 'N/A'),
            renter: item?.renter?.renterName || this.t('common.na', 'N/A'),
            month: this.buildPendingBillingMonthLabel(item?.billingMonths),
            monthKeys: this.extractPendingBillingMonthKeys(item?.billingMonths),
            amount: this.calculatePendingMappingAmount(item),
            mapping: item
          }));
          this.resetPendingVisibleRows();
          this.isPendingLoading = false;
        },
        error: () => {
          this.monthlyPendingRows = [];
          this.resetPendingVisibleRows();
          this.isPendingLoading = false;
        }
      });
  }

  private loadYearlyGraph(): void {
    this.isYearlyLoading = true;
    this.fetchYearlyGraphSource()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (this.yearlyMetric === 'collection') {
            const rows = Array.isArray(res?.data) ? res.data : [];
            this.yearlyCollectionRawRows = rows;
            const parsed = this.parseYearlyPaymentFromList(rows);
            this.yearlySeries = parsed.series;
            this.yearlyMonthBreakdowns = parsed.breakdowns;
            this.yearlyExpenseHeadBreakdowns = {};
          } else {
            this.yearlyCollectionRawRows = [];
            const parsedExpense = this.parseYearlyExpenseFromList(res?.data);
            this.yearlySeries = parsedExpense.series;
            this.yearlyMonthBreakdowns = this.buildExpenseBreakdowns(this.yearlySeries);
            this.yearlyExpenseHeadBreakdowns = parsedExpense.headBreakdowns;
          }
          this.isYearlyLoading = false;
        },
        error: () => {
          this.yearlyCollectionRawRows = [];
          this.yearlySeries = Array.from({ length: 12 }).map(() => 0);
          this.yearlyMonthBreakdowns = {};
          this.yearlyExpenseHeadBreakdowns = {};
          this.isYearlyLoading = false;
        }
      });
  }

  private fetchYearlyGraphSource(): Observable<any> {
    const yearText = `${this.yearlyGraphYear}`;
    if (this.yearlyMetric === 'expense') {
      const expenseFilters: Record<string, unknown> = {
        expenseYear: yearText,
        status: 1
      };
      if (this.yearlyHouseId) {
        expenseFilters['house.id'] = this.yearlyHouseId;
      }
      if (this.yearlyFlatId) {
        expenseFilters['flat.id'] = this.yearlyFlatId;
      }
      return this.expenses.list({
        pagination: false,
        pageNo: 0,
        rowCount: 0,
        withTotals: false,
        filters: expenseFilters
      });
    }

    const paymentFilters: Record<string, unknown> = {
      paymentYear: yearText,
      status: 1
    };
    if (this.yearlyHouseId) {
      paymentFilters['flatRenterMapping.flat.house.id'] = this.yearlyHouseId;
    }
    if (this.yearlyFlatId) {
      paymentFilters['flatRenterMapping.flat.id'] = this.yearlyFlatId;
    }

    return this.payments.list({
      pagination: false,
      pageNo: 0,
      rowCount: 0,
      withTotals: false,
      filters: paymentFilters
    });
  }

  private parseYearlyPaymentFromList(data: any): {
    series: number[];
    breakdowns: Record<number, {
      rent: number;
      gas: number;
      electric: number;
      water: number;
      service: number;
      other: number;
      advanceAdjusted: number;
      grandTotal: number;
    }>;
  } {
    const series = Array.from({ length: 12 }).map(() => 0);
    const breakdowns: Record<number, {
      rent: number;
      gas: number;
      electric: number;
      water: number;
      service: number;
      other: number;
      advanceAdjusted: number;
      grandTotal: number;
    }> = {};

    for (let i = 0; i < 12; i += 1) {
      breakdowns[i] = {
        rent: 0,
        gas: 0,
        electric: 0,
        water: 0,
        service: 0,
        other: 0,
        advanceAdjusted: 0,
        grandTotal: 0
      };
    }

    const rows = Array.isArray(data) ? data : [];
    rows.forEach((item: any) => {
      const monthNo = Number(item?.paymentMonth);
      if (!Number.isFinite(monthNo) || monthNo < 1 || monthNo > 12) {
        return;
      }

      const idx = monthNo - 1;
      const rent = Number(item?.rentAmount ?? 0);
      const gas = Number(item?.gasBillAmount ?? 0);
      const electric = Number(item?.electricBillAmount ?? 0);
      const water = Number(item?.waterBillAmount ?? 0);
      const service = Number(item?.serviceChargeAndOtherAmount ?? 0);
      const other = Number(item?.negativeChargeAmount ?? 0);
      const advanceAdjusted = Number(item?.advanceAdjustedAmount ?? 0);
      const fullGrand = (
        Number(rent) + Number(gas) + Number(electric) + Number(water) + Number(service) - Number(other) - Number(advanceAdjusted)
      );
      const rowGrand = this.isHouseRentOnly
        ? Math.max(0, Number(rent) - Number(other) - Number(advanceAdjusted))
        : fullGrand;

      series[idx] += Number.isFinite(rowGrand) ? rowGrand : 0;
      breakdowns[idx].rent += Number.isFinite(rent) ? rent : 0;
      breakdowns[idx].gas += this.isHouseRentOnly ? 0 : (Number.isFinite(gas) ? gas : 0);
      breakdowns[idx].electric += this.isHouseRentOnly ? 0 : (Number.isFinite(electric) ? electric : 0);
      breakdowns[idx].water += this.isHouseRentOnly ? 0 : (Number.isFinite(water) ? water : 0);
      breakdowns[idx].service += this.isHouseRentOnly ? 0 : (Number.isFinite(service) ? service : 0);
      breakdowns[idx].other += Number.isFinite(other) ? other : 0;
      breakdowns[idx].advanceAdjusted += Number.isFinite(advanceAdjusted) ? advanceAdjusted : 0;
    });

    for (let i = 0; i < 12; i += 1) {
      breakdowns[i].grandTotal = Number(series[i] ?? 0);
    }

    return { series, breakdowns };
  }

  private parseYearlyPaymentSummary(data: any): {
    series: number[];
    breakdowns: Record<number, {
      rent: number;
      gas: number;
      electric: number;
      water: number;
      service: number;
      other: number;
      advanceAdjusted: number;
      grandTotal: number;
    }>;
  } {
    const series = Array.from({ length: 12 }).map(() => 0);
    const breakdowns: Record<number, {
      rent: number;
      gas: number;
      electric: number;
      water: number;
      service: number;
      other: number;
      advanceAdjusted: number;
      grandTotal: number;
    }> = {};

    const raw = Array.isArray(data) ? (data[0] ?? {}) : {};
    for (let month = 1; month <= 12; month += 1) {
      const item = raw?.[`${month}`] ?? {};
      const rent = Number(item?.total_rent ?? 0);
      const gas = Number(item?.total_gas ?? 0);
      const electric = Number(item?.total_electric ?? 0);
      const water = Number(item?.total_water ?? 0);
      const service = Number(item?.total_service_and_other ?? item?.total_service ?? 0);
      const other = Number(item?.total_negative_charge ?? item?.total_other ?? 0);
      const advanceAdjusted = Number(item?.total_advance_adjusted ?? 0);
      const fullGrandTotal = Number(item?.grand_total ?? (rent + gas + electric + water + service - other - advanceAdjusted));
      const grandTotal = this.isHouseRentOnly ? Math.max(0, rent - other - advanceAdjusted) : fullGrandTotal;
      series[month - 1] = Number.isFinite(grandTotal) ? grandTotal : 0;
      breakdowns[month - 1] = {
        rent: Number.isFinite(rent) ? rent : 0,
        gas: this.isHouseRentOnly ? 0 : (Number.isFinite(gas) ? gas : 0),
        electric: this.isHouseRentOnly ? 0 : (Number.isFinite(electric) ? electric : 0),
        water: this.isHouseRentOnly ? 0 : (Number.isFinite(water) ? water : 0),
        service: this.isHouseRentOnly ? 0 : (Number.isFinite(service) ? service : 0),
        other: Number.isFinite(other) ? other : 0,
        advanceAdjusted: Number.isFinite(advanceAdjusted) ? advanceAdjusted : 0,
        grandTotal: Number.isFinite(grandTotal) ? grandTotal : 0
      };
    }

    return { series, breakdowns };
  }

  private buildExpenseBreakdowns(series: number[]): Record<number, {
    rent: number;
    gas: number;
    electric: number;
    water: number;
    service: number;
    other: number;
    advanceAdjusted: number;
    grandTotal: number;
  }> {
    const map: Record<number, {
      rent: number;
      gas: number;
      electric: number;
      water: number;
      service: number;
      other: number;
      advanceAdjusted: number;
      grandTotal: number;
    }> = {};

    for (let i = 0; i < 12; i += 1) {
      const value = Number(series[i] ?? 0);
      map[i] = {
        rent: 0,
        gas: 0,
        electric: 0,
        water: 0,
        service: 0,
        other: 0,
        advanceAdjusted: 0,
        grandTotal: Number.isFinite(value) ? value : 0
      };
    }
    return map;
  }

  private parseYearlyExpenseFromList(data: any): {
    series: number[];
    headBreakdowns: Record<number, Array<{ label: string; value: number; color: string }>>;
  } {
    const series = Array.from({ length: 12 }).map(() => 0);
    const monthHeadTotals: Record<number, Record<string, number>> = {};
    const rows = Array.isArray(data) ? data : [];

    rows.forEach((item: any) => {
      const monthNo = Number(item?.expenseMonth);
      if (!Number.isFinite(monthNo) || monthNo < 1 || monthNo > 12) {
        return;
      }
      const idx = monthNo - 1;
      const amount = Number(item?.expenseAmount ?? 0);
      if (!Number.isFinite(amount) || amount <= 0) {
        return;
      }

      series[idx] += amount;

      const headName = `${item?.expenseHead?.name ?? item?.expenseHeadName ?? this.t('dashboard.otherExpense', 'Other Expense')}`.trim()
        || this.t('dashboard.otherExpense', 'Other Expense');
      if (!monthHeadTotals[idx]) {
        monthHeadTotals[idx] = {};
      }
      monthHeadTotals[idx][headName] = Number(monthHeadTotals[idx][headName] ?? 0) + amount;
    });

    const palette = ['#1a98b6', '#32b87f', '#f2a93b', '#4e7de0', '#8f63d6', '#f07f6b', '#37a4dd', '#7e91b7'];
    const headBreakdowns: Record<number, Array<{ label: string; value: number; color: string }>> = {};
    for (let i = 0; i < 12; i += 1) {
      const entries = Object.entries(monthHeadTotals[i] ?? {})
        .filter(([, value]) => Number(value) > 0)
        .sort((a, b) => Number(b[1]) - Number(a[1]));

      headBreakdowns[i] = entries.map(([label, value], index) => ({
        label,
        value: Number(value),
        color: palette[index % palette.length]
      }));
    }

    return { series, headBreakdowns };
  }

  private parseYearlySeriesFromList(data: any): number[] {
    const result = Array.from({ length: 12 }).map(() => 0);
    const rows = Array.isArray(data) ? data : [];

    rows.forEach((item: any) => {
      const monthNo = Number(this.yearlyMetric === 'expense' ? item?.expenseMonth : item?.paymentMonth);
      if (!Number.isFinite(monthNo) || monthNo < 1 || monthNo > 12) {
        return;
      }
      const idx = monthNo - 1;
      const amount = this.yearlyMetric === 'expense'
        ? Number(item?.expenseAmount ?? 0)
        : Number(
          Number(item?.rentAmount ?? 0)
          + Number(item?.gasBillAmount ?? 0)
          + Number(item?.electricBillAmount ?? 0)
          + Number(item?.waterBillAmount ?? 0)
          + Number(item?.serviceChargeAndOtherAmount ?? 0)
          - Number(item?.negativeChargeAmount ?? 0)
          - Number(item?.advanceAdjustedAmount ?? 0)
        );
      result[idx] += Number.isFinite(amount) ? amount : 0;
    });

    return result;
  }

  private monthRange(year: number, monthZeroBased: number): { startRange: string; endRange: string } {
    const start = new Date(year, monthZeroBased, 1);
    const end = new Date(year, monthZeroBased + 1, 0);
    return {
      startRange: `${start.getFullYear()}-${`${start.getMonth() + 1}`.padStart(2, '0')}-${`${start.getDate()}`.padStart(2, '0')}`,
      endRange: `${end.getFullYear()}-${`${end.getMonth() + 1}`.padStart(2, '0')}-${`${end.getDate()}`.padStart(2, '0')}`
    };
  }

  private getPreviousMonthYear(month: number, year: number): { month: number; year: number } {
    if (month === 0) {
      return { month: 11, year: year - 1 };
    }
    return { month: month - 1, year };
  }

  private buildPendingBillingMonthLabel(billingMonths: Record<string, unknown> | null | undefined): string {
    const labels: string[] = [];
    const source = billingMonths ?? {};
    const years = Object.keys(source).sort((a, b) => Number(a) - Number(b));

    years.forEach((yearText) => {
      const monthsRaw = Array.isArray((source as Record<string, unknown>)[yearText])
        ? ((source as Record<string, unknown>)[yearText] as unknown[])
        : [];
      const months = monthsRaw
        .map((item) => Number(item))
        .filter((monthNo) => Number.isFinite(monthNo) && monthNo >= 1 && monthNo <= 12)
        .sort((a, b) => a - b);

      months.forEach((monthNo) => {
        labels.push(this.monthNames[monthNo - 1]);
      });
    });

    if (labels.length === 0) {
      return `${this.fullMonthNames[this.selectedMonth]} ${this.selectedYear}`;
    }
    return labels.join(', ');
  }

  private extractPendingBillingMonthKeys(billingMonths: Record<string, unknown> | null | undefined): string[] {
    const source = billingMonths ?? {};
    const keys: string[] = [];

    Object.keys(source)
      .sort((a, b) => Number(a) - Number(b))
      .forEach((yearText) => {
        const monthsRaw = Array.isArray((source as Record<string, unknown>)[yearText])
          ? ((source as Record<string, unknown>)[yearText] as unknown[])
          : [];

        monthsRaw
          .map((item) => Number(item))
          .filter((monthNo) => Number.isFinite(monthNo) && monthNo >= 1 && monthNo <= 12)
          .sort((a, b) => a - b)
          .forEach((monthNo) => {
            keys.push(`${yearText}-${`${monthNo}`.padStart(2, '0')}`);
          });
      });

    return keys;
  }

  private hasBothPendingRangeMonths(): boolean {
    const previous = this.getPreviousMonthYear(this.selectedMonth, this.selectedYear);
    const previousKey = `${previous.year}-${`${previous.month + 1}`.padStart(2, '0')}`;
    const currentKey = `${this.selectedYear}-${`${this.selectedMonth + 1}`.padStart(2, '0')}`;
    const availableKeys = new Set<string>();

    this.monthlyPendingRows.forEach((item) => {
      (item?.monthKeys ?? []).forEach((key) => {
        if (`${key}`.trim()) {
          availableKeys.add(`${key}`.trim());
        }
      });
    });

    return availableKeys.has(previousKey) && availableKeys.has(currentKey);
  }

  private calculatePendingMappingAmount(mapping: any): number {
    const flat = mapping?.flat ?? {};
    const monthsCount = this.countBillingMonths(mapping?.billingMonths);
    if (monthsCount <= 0) {
      return 0;
    }

    let perMonth =
      Number(flat?.rentAmount ?? 0)
      + (flat?.gasBillRequired ? Number(flat?.gasBillAmount ?? 0) : 0)
      + (flat?.electricBillRequired ? Number(flat?.electricBillAmount ?? 0) : 0)
      + (flat?.waterBillRequired ? Number(flat?.waterBillAmount ?? 0) : 0)
      + (flat?.serviceChargeAndOtherRequired ? Number(flat?.serviceChargeAndOtherAmount ?? 0) : 0)
      - (flat?.negativeChargeRequired ? Number(flat?.negativeChargeAmount ?? 0) : 0);

    if (!Number.isFinite(perMonth) || perMonth < 0) {
      perMonth = 0;
    }
    return perMonth * monthsCount;
  }

  private calculatePendingMappingRentAmount(mapping: any): number {
    const monthsCount = this.countBillingMonths(mapping?.billingMonths);
    if (monthsCount <= 0) {
      return 0;
    }
    const perMonthRent = Number(mapping?.flat?.rentAmount ?? 0);
    return (Number.isFinite(perMonthRent) ? Math.max(0, perMonthRent) : 0) * monthsCount;
  }

  private countBillingMonths(billingMonths: Record<string, unknown> | null | undefined): number {
    const source = billingMonths ?? {};
    return Object.keys(source).reduce((count, yearKey) => {
      const monthsRaw = Array.isArray((source as Record<string, unknown>)[yearKey])
        ? ((source as Record<string, unknown>)[yearKey] as unknown[])
        : [];
      return count + monthsRaw
        .map((item) => Number(item))
        .filter((monthNo) => Number.isFinite(monthNo) && monthNo >= 1 && monthNo <= 12)
        .length;
    }, 0);
  }

  private parseYearlySeries(data: any): number[] {
    const result = Array.from({ length: 12 }).map(() => 0);
    if (!data) {
      return result;
    }

    const monthIndexByName: Record<string, number> = {
      january: 0, jan: 0,
      february: 1, feb: 1,
      march: 2, mar: 2,
      april: 3, apr: 3,
      may: 4,
      june: 5, jun: 5,
      july: 6, jul: 6,
      august: 7, aug: 7,
      september: 8, sep: 8, sept: 8,
      october: 9, oct: 9,
      november: 10, nov: 10,
      december: 11, dec: 11
    };

    const setAt = (month: number, amount: any) => {
      const idx = Number(month) - 1;
      if (idx >= 0 && idx < 12) {
        result[idx] = Number(amount ?? 0);
      }
    };

    const assignFromMonthMap = (monthMap: Record<string, any>) => {
      Object.entries(monthMap || {}).forEach(([key, raw]) => {
        const numericMonth = Number(key);
        if (!Number.isFinite(numericMonth) || numericMonth < 1 || numericMonth > 12) {
          return;
        }
        const amount = typeof raw === 'object' && raw !== null
          ? (raw as any)?.amount
            ?? (raw as any)?.total
            ?? (raw as any)?.value
            ?? (raw as any)?.grand_total
            ?? (raw as any)?.total_expense
            ?? 0
          : raw ?? 0;
        setAt(numericMonth, amount);
      });
    };

    if (Array.isArray(data)) {
      data.forEach((item) => {
        const month = Number(item?.month ?? item?.paymentMonth ?? item?.monthNo);
        const amount = item?.amount ?? item?.total ?? item?.value ?? item?.collection;
        if (Number.isFinite(month)) {
          setAt(month, amount);
          return;
        }
        if (item && typeof item === 'object') {
          assignFromMonthMap(item as Record<string, any>);
        }
      });
      return result;
    }

    if (typeof data === 'object') {
      assignFromMonthMap(data as Record<string, any>);
      Object.entries(data).forEach(([key, raw]) => {
        const lower = key.toLowerCase();
        const numericMonth = Number(key);
        if (Number.isFinite(numericMonth) && numericMonth >= 1 && numericMonth <= 12) {
          if (typeof raw === 'object' && raw !== null) {
            const amount = (raw as any)?.amount ?? (raw as any)?.total ?? (raw as any)?.value ?? (raw as any)?.grand_total ?? (raw as any)?.total_expense;
            setAt(numericMonth, amount);
          } else {
            setAt(numericMonth, raw);
          }
          return;
        }

        if (monthIndexByName[lower] !== undefined) {
          const idx = monthIndexByName[lower];
          result[idx] = Number(typeof raw === 'object' && raw !== null
            ? (raw as any)?.amount ?? (raw as any)?.total ?? (raw as any)?.value ?? (raw as any)?.grand_total ?? (raw as any)?.total_expense ?? 0
            : raw ?? 0);
          return;
        }

        const matched = lower.match(/(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec|january|february|march|april|june|july|august|september|october|november|december)/);
        if (matched?.[1]) {
          const idx = monthIndexByName[matched[1]];
          result[idx] = Number(typeof raw === 'object' && raw !== null
            ? (raw as any)?.amount ?? (raw as any)?.total ?? (raw as any)?.value ?? (raw as any)?.grand_total ?? (raw as any)?.total_expense ?? 0
            : raw ?? 0);
        }
      });
    }

    return result;
  }

  private createYearOptions(): number[] {
    const current = new Date().getFullYear();
    const years: number[] = [];
    for (let year = current; year >= DASHBOARD_YEAR_MIN; year -= 1) {
      years.push(year);
    }
    return years;
  }

  private formatDateLabel(date: Date): string {
    const day = `${date.getDate()}`.padStart(2, '0');
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  private formatDateTimeLabel(date: Date): string {
    const day = `${date.getDate()}`.padStart(2, '0');
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const year = date.getFullYear();
    const hoursRaw = date.getHours();
    const minutes = `${date.getMinutes()}`.padStart(2, '0');
    const period = hoursRaw >= 12 ? 'pm' : 'am';
    const hour12 = hoursRaw % 12 || 12;
    const hourText = `${hour12}`.padStart(2, '0');
    return `${day}-${month}-${year} ${hourText}:${minutes} ${period}`;
  }

  private resolveCurrentUserLabel(): string {
    const token = `${this.tokens.accessToken ?? ''}`.trim();
    if (!token) {
      return this.t('common.na', 'N/A');
    }
    const parts = token.split('.');
    if (parts.length < 2) {
      return this.t('common.na', 'N/A');
    }

    try {
      const base64 = parts[1]
        .replace(/-/g, '+')
        .replace(/_/g, '/')
        .padEnd(Math.ceil(parts[1].length / 4) * 4, '=');
      const jsonText = atob(base64);
      const payload = JSON.parse(jsonText) as Record<string, unknown>;
      const label = `${payload?.['name'] ?? payload?.['username'] ?? payload?.['preferred_username'] ?? payload?.['sub'] ?? ''}`.trim();
      return label || this.t('common.na', 'N/A');
    } catch {
      return this.t('common.na', 'N/A');
    }
  }

  private loadCurrentUserFullName(): void {
    this.currentUserFullName = this.t('common.na', 'N/A');
    this.appUsers.me()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (profile: any) => {
          const fullName = `${profile?.name ?? ''}`.trim();
          if (fullName) {
            this.currentUserFullName = fullName;
            return;
          }
          this.loadCurrentUserFullNameFromList();
        },
        error: () => this.loadCurrentUserFullNameFromList()
      });
  }

  private loadCurrentUserFullNameFromList(): void {
    this.appUsers.basicInfoFromList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (profile: any) => {
          const fullName = `${profile?.name ?? ''}`.trim();
          this.currentUserFullName = fullName || this.resolveCurrentUserLabel();
        },
        error: () => {
          this.currentUserFullName = this.resolveCurrentUserLabel();
        }
      });
  }

  private loadYearlyReportBranding(): void {
    this.appUsers.me()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (profile: any) => {
          if (!profile) {
            this.loadYearlyReportBrandingFromListFallback();
            return;
          }
          this.resolveYearlyReportBrandingProfile(profile);
        },
        error: () => this.loadYearlyReportBrandingFromListFallback()
      });
  }

  private loadYearlyReportBrandingFromListFallback(): void {
    this.appUsers.basicInfoFromList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (profile: any) => {
          if (!profile) {
            this.yearlyReportBrandName = 'HTALLY';
            this.yearlyReportBrandLogoUrl = null;
            return;
          }
          this.resolveYearlyReportBrandingProfile(profile);
        },
        error: () => {
          this.yearlyReportBrandName = 'HTALLY';
          this.yearlyReportBrandLogoUrl = null;
        }
      });
  }

  private resolveYearlyReportBrandingProfile(profile: any): void {
    this.appUsers.resolveAccountOwnerProfile(profile)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (ownerProfile: any) => this.applyYearlyReportBranding(ownerProfile ?? profile),
        error: () => this.applyYearlyReportBranding(profile)
      });
  }

  private applyYearlyReportBranding(profile: any): void {
    const organizationName = `${profile?.organizationName ?? ''}`.trim();
    const userName = `${profile?.name ?? ''}`.trim();
    this.yearlyReportBrandName = organizationName || userName || 'HTALLY';

    const attachmentId = Number(profile?.attachments?.[0]?.id);
    if (!Number.isFinite(attachmentId)) {
      this.yearlyReportBrandLogoUrl = null;
      return;
    }

    this.attachments.previewUrlByAttachmentIdCached(attachmentId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (url: string) => {
          this.yearlyReportBrandLogoUrl = `${url ?? ''}`.trim() || null;
        },
        error: () => {
          this.yearlyReportBrandLogoUrl = null;
        }
      });
  }

  private yearMonthLabel(monthNo: number, year: number): string {
    const idx = Number(monthNo) - 1;
    const name = idx >= 0 && idx < this.monthNames.length ? this.monthNames[idx] : this.t('dashboard.unknownMonth', 'Unknown');
    const yearShort = `${year}`.slice(-2);
    return `${name}, ${yearShort}`;
  }

  private resetYearlyReportState(): void {
    const na = this.t('common.na', 'N/A');
    this.yearlyReportHouseLabel = na;
    this.yearlyReportHouseNo = na;
    this.yearlyReportHouseAddress = na;
    this.yearlyReportFlatLabel = na;
    this.yearlyReportFloorLabel = na;
    this.yearlyReportNegativeChargeTitle = this.t('dashboard.legend.negativeCharge', 'Negative Charge');
    this.yearlyReportGeneratedOn = na;
    this.yearlyReportGeneratedBy = na;
    this.currentUserFullName = na;
  }

  private t(key: string, fallback = ''): string {
    return this.language.translate(key, fallback);
  }

  private loadSubscriptionSummary(): void {
    this.subscriptionService.getSubscriptionSummary()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (summary) => {
          this.subscriptionSummary = summary;
        },
        error: () => {
          this.subscriptionSummary = null;
        }
      });
  }

  isPendingPaymentBlocked(): boolean {
    const expireDate = `${this.subscriptionSummary?.currentPackage?.expireDate ?? ''}`.trim();
    if (!expireDate) {
      return false;
    }
    const today = new Date();
    const todayIso = `${today.getFullYear()}-${`${today.getMonth() + 1}`.padStart(2, '0')}-${`${today.getDate()}`.padStart(2, '0')}`;
    return expireDate < todayIso;
  }

  private openPendingPaymentBlockedToast(): void {
    this.snackBar.open('Package expired. Please renew your package.', this.t('common.close', 'Close'), {
      duration: 3500,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['dashboard-toast-warning']
    });
  }

  private uniquePendingValues(key: 'house' | 'floor' | 'flat'): string[] {
    const unique = new Set<string>();
    this.monthlyPendingRows.forEach((item) => {
      const value = `${item?.[key] ?? ''}`.trim();
      if (value) {
        unique.add(value);
      }
    });
    return Array.from(unique.values()).sort((a, b) => a.localeCompare(b));
  }

  private resetPendingFilters(): void {
    this.pendingFilterHouse = '';
    this.pendingFilterFloor = '';
    this.pendingFilterFlat = '';
  }

  private resetPendingVisibleRows(): void {
    this.visiblePendingRowCount = this.pendingPageSize;
  }

  private applyMonthlySummaryMode(): void {
    this.collectionAmount = this.isHouseRentOnly ? this.monthlyCollectionRentAmount : this.monthlyCollectionGrandAmount;
    this.dueAmount = this.isHouseRentOnly ? this.monthlyDueRentAmount : this.monthlyDueGrandAmount;
    this.netCollection = this.collectionAmount - this.expenseAmount;
  }
}

